import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Button } from './ui/button';
import { LogOut, LayoutDashboard, Bell, Crown, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = () => {
  const { t } = useLanguage();
  const { isAuthenticated, logout, user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [dmUnreadCount, setDmUnreadCount] = useState(0);

  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: getAuthHeaders()
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch {
      // Silently fail
    }
    try {
      const dm = await axios.get(`${API}/director/dms`, { headers: getAuthHeaders() });
      const total = (dm.data?.threads || []).reduce((s, t) => s + (t.school_unread || 0), 0);
      setDmUnreadCount(total);
    } catch {
      // Silently fail
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (isAuthenticated) {
      loadUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, loadUnreadCount]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Don't show navbar on generated website pages, admin, or pages with sidebar layout
  const pagesWithSidebar = [
    '/dashboard', '/school/', '/student/', '/teacher/', '/parent/', 
    '/settings/', '/ubo', '/offline-content', '/inbox', '/council'
  ];
  
  const hasSidebarLayout = pagesWithSidebar.some(p => location.pathname.startsWith(p));
  
  const uboPublic = ['/', '/standard', '/schools', '/families', '/council', '/evidence', '/about', '/framework', '/authorization', '/assessment', '/educators', '/research'];
  if (uboPublic.includes(location.pathname) ||
      location.pathname.startsWith('/schools/') || 
      location.pathname.startsWith('/admin') ||
      location.pathname.startsWith('/pay/') ||
      location.pathname.startsWith('/adhesion') ||
      location.pathname.startsWith('/live/') ||
      location.pathname.startsWith('/resultats') ||
      location.pathname.startsWith('/standard') ||
      location.pathname.startsWith('/framework') ||
      location.pathname.startsWith('/authorization') ||
      location.pathname.startsWith('/assessment') ||
      location.pathname.startsWith('/educators') ||
      location.pathname.startsWith('/research') ||
      location.pathname.startsWith('/schools') ||
      location.pathname.startsWith('/council') ||
      location.pathname.startsWith('/families') ||
      location.pathname.startsWith('/evidence') ||
      location.pathname.startsWith('/about') ||
      location.pathname.startsWith('/familles') ||
      location.pathname.startsWith('/portail') ||
      location.pathname === '/' ||
      location.pathname === '/switch' ||
      hasSidebarLayout) {
    return null;
  }

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="navbar-logo">
            <img 
              src="/logo-ubo.png" 
              alt="Universal Baccalaureate" 
              className="h-10 object-contain"
            />
          </Link>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="ghost" />
            
            {isAuthenticated ? (
              <>
                <Link to="/council">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="gap-2 text-academic-gold-600 hover:text-academic-gold-700 hover:bg-academic-gold-50"
                    data-testid="nav-council"
                  >
                    <Crown className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('nav.council')}</span>
                  </Button>
                </Link>
                <Link to="/inbox">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="gap-2 relative"
                    data-testid="nav-inbox"
                  >
                    <Bell className="h-4 w-4" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </Button>
                </Link>
                <Link to="/messages">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 relative"
                    data-testid="nav-messages"
                  >
                    <MessageSquare className="h-4 w-4" />
                    {dmUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-rose-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium">
                        {dmUnreadCount > 9 ? '9+' : dmUnreadCount}
                      </span>
                    )}
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="nav-dashboard">
                    <LayoutDashboard className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('nav.dashboard')}</span>
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleLogout}
                  className="gap-2 text-slate-600 hover:text-red-600"
                  data-testid="nav-logout"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('nav.logout')}</span>
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" data-testid="nav-login">
                    {t('nav.login')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    size="sm" 
                    className="bg-oxford-700 hover:bg-oxford-800 text-white rounded-full px-4"
                    data-testid="nav-register"
                  >
                    {t('nav.register')}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};
